<template>
  <div class="w-100 pa-2">
    <button data-id-e2e="layoutExpandButton" class="layout-button" @click="expandAll()">
      <v-icon size="16" class="ml-1"> mdi-arrow-expand </v-icon>
      {{ tkey('expandAll') }}
    </button>
    <button data-id-e2e="layoutCollapseButton" class="layout-button" @click="collapseAll()">
      <v-icon size="16" class="ml-1"> mdi-arrow-collapse </v-icon>
      {{ tkey('collapseAll') }}
    </button>

    <div data-id-e2e="layoutGrid" class="w-100 fill-height overflow-hidden">
      <ProductLayoutsOptions :selected-layout-name="selectedLayoutName" @save="saveLayout" />
      <AgGridVue
        class="w-100 h-100"
        :column-defs="columnDefs"
        :row-data="layoutData"
        :group-display-type="groupDisplayType"
        :group-default-expanded="-1"
        :grid-options="gridOptions"
        @grid-ready="onGridReady"
      />
    </div>

    <v-dialog
      v-model="isDeleteLayoutModalOpen"
      content-class="rtls-dialog rtls-dialog--message"
      :width="416"
    >
      <BaseDialogCard
        :title="tkey('deleteLayoutModalTitle')"
        is-popup
        @close="closeDeleteLayoutModal"
      >
        <v-container fluid class="pa-0 pt-4">
          <div>{{ deleteLayoutModalMessage }}</div>
          <div>{{ tkey('deleteLayoutModalSubMessage') }}</div>
        </v-container>
        <template #footer>
          <div class="d-flex justify-end pa-0">
            <v-btn secondary variant="flat" class="outlined-btn" @click="closeDeleteLayoutModal">
              {{ t('actions.cancel') }}
            </v-btn>
            <v-btn class="ml-4" primary @click="confirmDeleteLayout">
              {{ t('actions.delete') }}
            </v-btn>
          </div>
        </template>
      </BaseDialogCard>
    </v-dialog>
  </div>
</template>

<script>
import { find, get } from 'lodash';
import AgGridLayoutRow from '@/components/AgGridLayoutRow.vue';
import { useReportingStore } from '@/stores/reporting';
import { useInternationalization } from '@/composables/use-internationalization';
import assortmentFlatGridTheme from '@/plugins/ag-grid/ag-grid-flat-theme';

export default {
  components: {
    /* eslint-disable vue/no-unused-components */
    AgGridLayoutRow,
  },

  setup() {
    const { t, tkey } = useInternationalization(
      'reportingPage.sections.product.sideBar.toolPanels.layouts'
    );

    const reportingStore = useReportingStore();

    return {
      t,
      tkey,
      reportingStore,
    };
  },

  data() {
    return {
      gridOptions: {
        theme: assortmentFlatGridTheme,
        columnMenu: 'legacy',
        rowHeight: 40,
        headerHeight: 0,
        suppressContextMenu: true,
      },
      columnDefs: [
        {
          field: 'layoutType',
          rowGroup: true,
          headerClass: 'd-none',
          cellClass: 'w-100 d-flex outline-none',
          valueFormatter: params => {
            const headerValue = this.tkey(`types.${params.value}`);
            return headerValue;
          },
          cellRenderer: 'AgGridLayoutRow',
          cellRendererParams: params => {
            return {
              layout: params.data,
              selectLayoutFunction: this.selectLayout,
              onDeleteLayout: this.onDeleteLayout,
            };
          },
        },
      ],

      gridApi: null,
      groupDisplayType: 'groupRows',
      selectedLayout: null,
      layoutToDelete: {
        name: null,
        id: null,
      },

      isDeleteLayoutModalOpen: false,
    };
  },

  computed: {
    layoutData() {
      return this.reportingStore.getLayouts;
    },

    selectedLayoutName() {
      const layout = find(this.layoutData, { _id: this.selectedLayoutId });
      return get(layout, 'name', this.t('general.default'));
    },

    selectedLayoutId: {
      get() {
        return this.selectedLayout;
      },
      set(newValue) {
        this.selectedLayout = newValue;
      },
    },

    deleteLayoutModalMessage() {
      return this.tkey('deleteLayoutModalMessage', [this.layoutToDelete.name]);
    },
  },

  methods: {
    async fetchData() {
      await this.reportingStore.fetchLayoutsData();
    },

    saveLayout(params) {
      this.params.saveLayout(params);
    },

    selectLayout(selectedLayoutId) {
      this.selectedLayout = selectedLayoutId;

      const { layout } = find(this.layoutData, { _id: selectedLayoutId });
      this.params.applyColumnLayout(layout);
    },

    onGridReady(params) {
      this.gridApi = params.api;
      this.fetchData();
    },

    expandAll() {
      this.gridApi.expandAll();
    },

    collapseAll() {
      this.gridApi.collapseAll();
    },

    onDeleteLayout({ name, _id }) {
      this.layoutToDelete = { name, id: _id };
      this.isDeleteLayoutModalOpen = true;
    },

    confirmDeleteLayout() {
      this.reportingStore.deleteLayout({ layoutId: this.layoutToDelete.id });
      this.isDeleteLayoutModalOpen = false;
      this.layoutToDelete = { name: null, id: null };
    },

    closeDeleteLayoutModal() {
      this.isDeleteLayoutModalOpen = false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.layout-button {
  text-decoration: underline;
  color: $assortment-primary-colour;
}

:deep() {
  .ag-center-cols-container {
    width: 100% !important;
  }
}
</style>
